<template>
  <q-btn
    no-caps
    color="grey-5"
    outline
    dense
    style="font-size: small"
    :style="btnStyle"
    class="q-py-sm q-px-md"
    :class="!$q.dark.isActive ? 'bg-white' : ''"
  >
    <q-icon
      name="event"
      class="button-content"
      size="xs"
      :class="!$q.dark.isActive ? 'text-black' : 'text-white'"
    />
    <div
      v-if="dateRangeLabel"
      class="q-ml-sm button-content"
      :class="!$q.dark.isActive ? 'text-black' : 'text-white'"
    >
      {{ dateRangeLabel }}
    </div>
    <div
      v-else
      class="q-ml-sm button-content"
      :class="!$q.dark.isActive ? 'text-black' : 'text-white'"
    >
      {{ btnLabel }}
    </div>
    <q-menu transition-duration="100">
      <div class="q-pl-md q-pt-md title">{{ title }}</div>
      <q-date
        v-model="value"
        flat
        square
        minimal
        today-btn
        range
        :options="useDateConstraints ? dateConstraints : undefined"
        mask="YYYY-MM-DD"
        @update:model-value="updateModelValue"
      />
      <q-separator />
      <div v-if="footer" class="row" style="width: 100%">
        <q-btn unelevated no-caps square class="col-3 q-px-xs" @click="lastXDay(7)">7D</q-btn>
        <q-btn unelevated no-caps square class="col-3 q-px-xs" @click="lastXDay(14)">14D</q-btn>
        <q-btn unelevated no-caps square class="col-3 q-px-xs" @click="lastXDay(30)">30D</q-btn>
        <q-btn unelevated no-caps square class="col-3 q-px-xs" @click="lastXDay(60)">2M</q-btn>
        <q-btn unelevated no-caps square class="col-3 q-px-xs" @click="lastXDay(90)">3M</q-btn>
        <q-btn unelevated no-caps square class="col-3 q-px-xs" @click="lastXDay(180)">6M</q-btn>
        <q-btn unelevated no-caps square class="col-3 q-px-xs" @click="lastXDay(365)">1Y</q-btn>
        <q-btn unelevated no-caps square class="col-3 q-px-xs" @click="allDays()">All</q-btn>
      </div>
    </q-menu>
  </q-btn>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { format, addDays, parseISO } from 'date-fns'

export default {
  name: 'DateRangePicker',
  components: {},
  props: {
    title: {
      type: String,
      required: true
    },
    dateRange: {
      type: Object as PropType<{ from: null | Date; to: null | Date }>,
      default() {
        return {
          from: null,
          to: null
        }
      }
    },
    btnStyle: {
      type: String,
      default: ''
    },
    btnLabel: {
      type: String,
      default: 'Date Range'
    },
    useDateConstraints: {
      type: Boolean,
      default: true
    },
    footer: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update'],
  data() {
    return {
      value: { from: format(new Date(), 'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') } as
        | { from: string; to: string }
        | string
        | null
    }
  },
  computed: {
    dateRangeLabel() {
      if (this.value && typeof this.value === 'object') {
        return `${format(parseISO(this.value.from), 'M/d/yy')} → ${format(parseISO(this.value.to), 'M/d/yy')}`
      } else if (this.value) {
        return format(parseISO(this.value), 'M/d/yy')
      }
      return ''
    }
  },
  watch: {
    dateRange() {
      this.dateRangeChange()
    }
  },
  mounted() {
    this.dateRangeChange()
  },
  methods: {
    dateConstraints(date: string) {
      return date >= '2023/06/17' && date <= format(new Date(), 'yyyy/MM/dd')
    },
    lastXDay(numDays: number) {
      const to = new Date()
      const from = addDays(to, -numDays)
      const dateRange = { from, to }
      this.$emit('update', dateRange)
    },
    allDays() {
      const to = new Date()
      const from = new Date('2023/06/17')
      const dateRange = { from, to }
      this.$emit('update', dateRange)
    },
    dateRangeChange() {
      if (this.dateRange.to && this.dateRange.from) {
        if (this.dateRange.to.toDateString() === this.dateRange.from.toDateString()) {
          this.value = format(this.dateRange.to, 'yyyy-MM-dd')
        } else {
          this.value = {
            to: format(this.dateRange.to, 'yyyy-MM-dd'),
            from: format(this.dateRange.from, 'yyyy-MM-dd')
          }
        }
      }
    },
    updateModelValue() {
      let minDate = null as Date | null
      let maxDate = null as Date | null
      if (this.value && typeof this.value === 'object') {
        minDate = this.value.from ? parseISO(this.value.from) : null
        maxDate = this.value.to ? parseISO(this.value.to) : null
      } else if (this.value) {
        minDate = parseISO(this.value)
        maxDate = parseISO(this.value)
      }
      if (minDate && maxDate) {
        this.$emit('update', { from: minDate, to: maxDate })
      }
    }
  }
}
</script>

<style scoped>
.title {
  font-size: 17px;
  font-weight: 500;
}
.button-content {
  font-weight: 400;
}
.q-date__view {
  min-height: inherit;
}
</style>
